import { Component } from '@theme/component';

const KLAVIYO_WRITE_REVIEW_SELECTOR =
  '#klaviyo-product-reviews-wrapper .kl_reviews__button:nth-child(1)';

/**
 * WS product reviews — toggles pre-rendered panels; Klaviyo submit uses on-page widget.
 *
 * @extends Component
 */
export class WsProductReviews extends Component {
  /** @type {number} */
  #scrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown);

    if (this.dataset.autoOpenUrlParam === 'true') {
      const param = this.dataset.urlParam || 'write_review';
      if (new URLSearchParams(window.location.search).has(param)) {
        requestAnimationFrame(() => this.openSubmitReview());
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#unlockScroll();
  }

  openReviewsList() {
    this.#closePanel('form');
    this.#openPanel('list');
  }

  openSubmitReview() {
    this.#closePanel('list');

    const mode = this.dataset.submitMode || 'klaviyo';

    if (mode === 'klaviyo') {
      this.#triggerKlaviyoWriteReview();
      return;
    }

    this.#openPanel('form');
  }

  closeAllPanels() {
    this.#closePanel('list');
    this.#closePanel('form');
    this.#unlockScroll();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#isAnyPanelOpen()) return;
    event.preventDefault();
    this.closeAllPanels();
  };

  /**
   * @param {string} name
   */
  #openPanel(name) {
    const overlay = this.querySelector(`[data-panel="${name}"]`);
    if (!overlay) return;

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    this.#lockScroll();

    const panel = overlay.querySelector('.ws-product-reviews__panel');
    if (panel instanceof HTMLElement) {
      panel.focus();
    }
  }

  /**
   * @param {string} name
   */
  #closePanel(name) {
    const overlay = this.querySelector(`[data-panel="${name}"]`);
    if (!overlay || !overlay.classList.contains('is-open')) return;

    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');

    if (!this.#isAnyPanelOpen()) {
      this.#unlockScroll();
    }
  }

  #isAnyPanelOpen() {
    return Boolean(this.querySelector('.ws-product-reviews__overlay.is-open'));
  }

  #lockScroll() {
    if (document.body.style.position === 'fixed') return;

    this.#scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #unlockScroll() {
    if (document.body.style.position !== 'fixed') return;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
    document.documentElement.removeAttribute('scroll-lock');
  }

  #triggerKlaviyoWriteReview() {
    const clickButton = () => {
      const button = document.querySelector(KLAVIYO_WRITE_REVIEW_SELECTOR);
      if (button instanceof HTMLElement) {
        button.click();
        return true;
      }
      return false;
    };

    if (clickButton()) return;

    const observer = new MutationObserver(() => {
      if (clickButton()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  }
}

if (!customElements.get('ws-product-reviews')) {
  customElements.define('ws-product-reviews', WsProductReviews);
}
